import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { API_URL } from '../../../config';
import { useMembershipAccess } from '../../../hooks/membership';
import { Dialog } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';

const ACTIVE_STATUSES = new Set(['queued', 'processing']);

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value, language) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

function formatCount(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

const DataExportPanel = () => {
  const { t, i18n } = useTranslation();
  const access = useMembershipAccess();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requestedFiles, setRequestedFiles] = useState({
    investors: true,
    deals: true,
  });

  const isProfessional = access.isProfessional;
  const activeJob = useMemo(
    () => jobs.find((job) => ACTIVE_STATUSES.has(job.status)),
    [jobs]
  );
  const latestJob = jobs[0] || null;

  const getRequestErrorMessage = useCallback((requestError, fallbackKey) => {
    const status = requestError?.response?.status;
    if (status === 403) return t('app:dashboard.dataExport.errors.professionalOnly');
    if (status === 409) return t('app:dashboard.dataExport.errors.alreadyActive');
    if (status === 410) return t('app:dashboard.dataExport.errors.expired');
    if (status === 429) return t('app:dashboard.dataExport.errors.rateLimit');
    return t(fallbackKey);
  }, [t]);

  const fetchJobs = useCallback(async ({ silent = false } = {}) => {
    if (!isProfessional) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/data-exports/jobs`, {
        withCredentials: true,
      });
      setJobs(Array.isArray(response.data?.jobs) ? response.data.jobs : []);
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, 'app:dashboard.dataExport.errors.load'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getRequestErrorMessage, isProfessional]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (!isProfessional || !jobs.some((job) => ACTIVE_STATUSES.has(job.status))) return undefined;
    const timer = window.setInterval(() => {
      fetchJobs({ silent: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [fetchJobs, isProfessional, jobs]);

  const toggleFile = (key) => {
    setRequestedFiles((current) => {
      return { ...current, [key]: !current[key] };
    });
  };

  const selectedCount = Object.values(requestedFiles).filter(Boolean).length;

  const createExport = async () => {
    if (selectedCount === 0) return;
    setCreating(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_URL}/data-exports/jobs`,
        {
          requestedFiles,
          locale: i18n.language,
        },
        { withCredentials: true }
      );
      setDialogOpen(false);
      setJobs((current) => [response.data.job, ...current.filter((job) => job.id !== response.data.job.id)]);
      fetchJobs({ silent: true });
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, 'app:dashboard.dataExport.errors.create'));
    } finally {
      setCreating(false);
    }
  };

  const downloadJob = async (job) => {
    if (!job?.downloadUrl) return;
    setDownloadingId(job.id);
    setError('');
    try {
      const response = await axios.get(`${API_URL}${job.downloadUrl}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = job.fileName || 'crino-data-export.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      fetchJobs({ silent: true });
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, 'app:dashboard.dataExport.errors.download'));
    } finally {
      setDownloadingId(null);
    }
  };

  const statusLabel = (status) => t(`app:dashboard.dataExport.status.${status}`, {
    defaultValue: status,
  });

  const renderJobStatus = (job) => {
    if (!job) return null;

    const isActive = ACTIVE_STATUSES.has(job.status);
    const isReady = job.status === 'completed' && job.downloadUrl;
    const isFailed = job.status === 'failed';

    return (
      <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50/70 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-stone-900">
              {isActive ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isReady ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : null}
              {isFailed ? <AlertCircle className="h-4 w-4 text-red-600" /> : null}
              {!isActive && !isReady && !isFailed ? <Archive className="h-4 w-4" /> : null}
              <span>{statusLabel(job.status)}</span>
            </div>
            <p className="mt-1 text-xs leading-5 text-stone-600">
              {isReady
                ? t('app:dashboard.dataExport.readyFile')
                : t('app:dashboard.dataExport.preparingFile')}
            </p>
            {job.expiresAt && job.status === 'completed' ? (
              <p className="mt-1 text-xs leading-5 text-stone-600">
                {t('app:dashboard.dataExport.expiresAt', {
                  date: formatDate(job.expiresAt, i18n.language),
                })}
              </p>
            ) : null}
            {job.error ? (
              <p className="mt-2 text-xs leading-5 text-red-600">
                {t('app:dashboard.dataExport.errors.jobFailed')}
              </p>
            ) : null}
          </div>

          {isReady ? (
            <Button
              type="button"
              size="sm"
              iconLeft={downloadingId === job.id ? <Loader2 className="animate-spin" /> : <Download />}
              onClick={() => downloadJob(job)}
              disabled={downloadingId === job.id}
              className="shrink-0"
            >
              {t('app:dashboard.dataExport.download')}
            </Button>
          ) : null}
        </div>

        <div className="mt-4 grid gap-2 text-xs text-stone-600 sm:grid-cols-3">
          <span>{t('app:dashboard.dataExport.counts.investors', { count: formatCount(job.counts?.investors) })}</span>
          <span>{t('app:dashboard.dataExport.counts.deals', { count: formatCount(job.counts?.deals) })}</span>
          <span>
            {formatBytes(job.sizeBytes)
              ? t('app:dashboard.dataExport.fileSize', { size: formatBytes(job.sizeBytes) })
              : t('app:dashboard.dataExport.fileSizePending')}
          </span>
        </div>
      </div>
    );
  };

  if (!isProfessional) {
    return (
      <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
          <Lock className="h-3.5 w-3.5" />
          {t('app:dashboard.dataExport.badge')}
        </div>
        <h3 className="text-xl font-semibold tracking-tight text-stone-950">
          {t('app:dashboard.dataExport.title')}
        </h3>
        <p className="mt-2 text-sm leading-6 text-stone-600">
          {t('app:dashboard.dataExport.lockedDescription')}
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <Archive className="h-3.5 w-3.5" />
            {t('app:dashboard.dataExport.badge')}
          </div>
          <h3 className="text-xl font-semibold tracking-tight text-stone-950">
            {t('app:dashboard.dataExport.title')}
          </h3>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {t('app:dashboard.dataExport.description')}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconLeft={loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            onClick={() => fetchJobs()}
            disabled={loading}
          >
            {t('app:dashboard.dataExport.refresh')}
          </Button>
          <Button
            type="button"
            size="sm"
            iconLeft={<FileSpreadsheet />}
            onClick={() => setDialogOpen(true)}
            disabled={Boolean(activeJob)}
          >
            {t('app:dashboard.dataExport.newExport')}
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
        {t('app:dashboard.dataExport.expiryNotice')}
      </div>

      {error ? (
        <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading && jobs.length === 0 ? (
        <div className="mt-4 text-sm text-stone-500">
          {t('app:dashboard.dataExport.loading')}
        </div>
      ) : renderJobStatus(latestJob)}

      {!loading && jobs.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-stone-300 bg-stone-50/70 px-4 py-3 text-sm leading-6 text-stone-600">
          {t('app:dashboard.dataExport.empty')}
        </div>
      ) : null}

      <Dialog
        isOpen={dialogOpen}
        onClose={() => !creating && setDialogOpen(false)}
        title={t('app:dashboard.dataExport.dialog.title')}
        subtitle={t('app:dashboard.dataExport.dialog.subtitle')}
        size="lg"
        footerActions={(
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDialogOpen(false)}
              disabled={creating}
            >
              {t('app:dashboard.dataExport.dialog.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              iconLeft={creating ? <Loader2 className="animate-spin" /> : <Archive />}
              onClick={createExport}
              disabled={creating || selectedCount === 0}
            >
              {t('app:dashboard.dataExport.dialog.create')}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3">
          {[
            ['investors', 'investorsFile'],
            ['deals', 'dealsFile'],
          ].map(([key, labelKey]) => (
            <label
              key={key}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 bg-stone-50/70 p-4"
            >
              <Checkbox
                checked={requestedFiles[key]}
                onCheckedChange={() => toggleFile(key)}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium text-stone-900">
                  {t(`app:dashboard.dataExport.dialog.${labelKey}.title`)}
                </span>
                <span className="mt-1 block text-xs leading-5 text-stone-600">
                  {t(`app:dashboard.dataExport.dialog.${labelKey}.description`)}
                </span>
              </span>
            </label>
          ))}
        </div>

        <div className="mt-4 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm leading-6 text-stone-600">
          {t('app:dashboard.dataExport.dialog.securityNote')}
        </div>
      </Dialog>
    </section>
  );
};

export default DataExportPanel;
